import React, {useEffect, useMemo, useState} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import {
    Alert,
    Box,
    Link,
    Typography
} from '@mui/material'
import {searchTransferRequests, SearchTransferResult} from '../transferUtils'
import CheckIcon from '@mui/icons-material/Check'
import {DataGrid, GridColDef} from '@mui/x-data-grid'
import {webEvents} from '../../common/WebEvents'
import {createError} from '../../store/slices/error'
import {useDispatch} from 'react-redux'

type TransferRequestReviewRows = {
    id: number,
    requesting_tech: string,
    requesting_truck: string,
    item: string,
    location: string,
    date: Date | null,
    fulfilling_tech: string | null,
    fulfilling_truck: string,
    status: string,
}

type SearchTransferPayload = {
    to_locations?: string,
    from_locations?: string,
    statuses: string,
    page_size?: number
}

const TransferRequestsReviewDashboard = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const dispatch = useDispatch()
    const [rows, setRows] = useState<TransferRequestReviewRows[]>([])
    const [alertOpen, setAlertOpen] = useState(false)
    const [reviewDashboardData, setReviewDashboardData] = useState<SearchTransferResult[]>([])

    const getTransferRequests = (payload: SearchTransferPayload, userType: string) => {
        searchTransferRequests(payload)
            .then((t: SearchTransferResult[]) => {
                if (t.length) {
                    if (userType === 'rm') {
                        setReviewDashboardData(t)
                    }
                }
            })
            .catch(error => {
                const causeMessage = error.cause?.messages?.[0] || error.messages?.[0] || "Unknown error"
                webEvents.error(
                    causeMessage,
                    {'Fetching transfer Request': ''},
                    error.stack
                )
                dispatch(createError({
                    type: error.type,
                    message: causeMessage
                }))
            })
    }


    const formatStatus = (inputStr: string) => {
        const words = inputStr.split('-')
        const formattedStr = words
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');

        return formattedStr
    }

    const setUpDashboardRows = (t: SearchTransferResult[]) => {
        const prepRows = t.map((data) => ({
            id: data.transferRequestId,
            requesting_tech: data.contactName,
            requesting_truck: data.toLocationId,
            item: data.sku,
            location: `${data.city}, ${data.state}`,
            date: data.status === 'pending-selection' ? data.approvedAt : data.requestedAt,
            fulfilling_tech: data.fromContactName,
            fulfilling_truck: data.fromLocationId,
            status: formatStatus(data.status)
        }))
        prepRows.sort((status1, status2) => {
            if (status1.status === 'in-review' && status2.status !== 'in-review') {
                return -1;
            }
            if (status1.status !== 'in-review' && status2.status === 'in-review') {
                return 1;
            }
            // If both have same status, use default date sorting
            if (status1.date && status2.date) {
                return new Date(status1.date).getTime() - new Date(status2.date).getTime()
            }
            return 0
        })
        setRows(prepRows)
    }

    useEffect(() => {
        if (location.state?.submissionSuccess) {
            setAlertOpen(true)
        }
        if (location.state?.dashboardData) {
            setReviewDashboardData(location.state?.dashboardData)
            setUpDashboardRows(reviewDashboardData)
        } else {
            const rmDashboardPayload = {statuses: 'in-review,pending-selection', page_size: 1000}
            getTransferRequests(rmDashboardPayload, 'rm')
        }
        return () => {
            setAlertOpen(false)
        }
    }, [location.state])

    useEffect(() => {
        if (reviewDashboardData.length) {
            setUpDashboardRows(reviewDashboardData)
        }
    }, [reviewDashboardData])

    const columns: GridColDef[] = [
        {field: 'id', headerName: 'ID', flex: 1},
        {field: 'requesting_tech', headerName: 'Requesting Tech', flex: 1},
        {field: 'requesting_truck', headerName: 'Requesting Truck', flex: 1},
        {field: 'item', headerName: 'Item', flex: 1},
        {field: 'location', headerName: 'Location', flex: 1},
        {field: 'date', headerName: 'Requested', flex: 1},
        {field: 'fulfilling_tech', headerName: 'Fulfilling Tech', flex: 1},
        {field: 'fulfilling_truck', headerName: 'Fulfilling Truck', flex: 1},
        {
            field: 'action', headerName: 'Action', flex: 1, renderCell: (params) => (
                params.row.status === 'In Review' && <Link
                    component="button"
                    variant="body2"
                    onClick={() => navigate(`/transfer-requests/manage/${params.row.id}`)}
                    >
                        Review
                    </Link>
        )
        },
        {field: 'status', headerName: 'Status', flex: 1}
    ]


    return (
        <>
            {alertOpen && (
                <Alert
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '80%',
                        mx: 'auto',
                        alignItems: 'flex-start',
                        my: 1
                    }}
                    icon={<CheckIcon fontSize="inherit"/>} severity="success"
                    onClose={() => {
                        location.state.submissionSuccess = false
                        setAlertOpen(false)
                    }}>
                    Approved Transfer Request successfully!
                </Alert>
            )}
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <Typography variant="h3" component="h2" gutterBottom
                            sx={{flexGrow: 1, textAlign: 'center', mb: 2, mt: 2}}>
                    Transfer Requests
                </Typography>
            </Box>

            <Box sx={{display: 'flex', flexDirection: 'column', width: '100%', mb: 2}}>
                <Typography sx={{textAlign: 'center', mb: 1}}>
                    Transfer Requests in Review
                </Typography>

                <Box sx={{display: 'flex', justifyContent: 'center', width: '100%'}}>
                    <div style={{height: 600, width: '80%', backgroundColor: 'whitesmoke'}}>
                        <DataGrid
                            rows={rows}
                            columns={columns}
                            disableRowSelectionOnClick
                            initialState={{
                                sorting: {
                                    sortModel: [{field: 'status', sort: 'desc'}]
                                }
                            }}
                        />
                    </div>
                </Box>
            </Box>
        </>

    )
}

export default TransferRequestsReviewDashboard
